import type { ReactNode } from "react";

import { SELECT_CHROME, SelectChevron } from "../controls/SelectChevron.js";
import { cn } from "../lib/cn.js";

/**
 * A select that narrows what a page or a section is showing.
 *
 * ==========================================================================================
 * IT DRAWS ITS OWN CHEVRON, AND THAT IS A PLATFORM CONSTRAINT RATHER THAN A PREFERENCE
 * ==========================================================================================
 *
 * A native `<select>`'s arrow is painted by the platform against the BORDER BOX, and `padding-right`
 * moves only the text — so the arrow cannot be inset. The glyph and the geometry that places it now
 * live in `SelectChevron`, shared with `SelectField`: the two selects must sit identically on a page
 * that shows both, and the whole argument for the drawn arrow is in that file.
 *
 * ==========================================================================================
 * NO FILL, AND THE PANEL'S OWN HAIRLINE
 * ==========================================================================================
 *
 * `border-line` rather than `border-input`, and no background at all. A filter is CHROME: it says
 * what you are looking at rather than collecting something, so the surface behind it — the page's
 * gradient, or a panel — should pass through. A filled control in a title row reads as a field
 * somebody forgot to complete.
 *
 * `[&>option]:bg-page` because a transparent select on a dark theme opens a menu the platform paints
 * against the page's default white, and the options are unreadable until they are given a surface.
 *
 * ==========================================================================================
 * THE LABEL IS ALWAYS THERE, AND USUALLY INVISIBLE
 * ==========================================================================================
 *
 * `label` is required and becomes the accessible name. `showLabel` renders it beside the control as
 * well, which a section header wants — several filters in a row are unreadable as bare dropdowns —
 * while a title row usually does not, because the page's heading is already the subject.
 *
 * A real `<label>` element AND `aria-label`, which is redundant on purpose: the element is what a
 * sighted reader sees when `showLabel` is on, and the attribute is what survives when it is off.
 */
/**
 * The shape every "what am I looking at" control takes, shared so they cannot drift apart.
 *
 * ==========================================================================================
 * IT CARRIES NO COLOUR, AND THAT IS DELIBERATE RATHER THAN INCOMPLETE
 * ==========================================================================================
 *
 * `cn` is a plain join, not `tailwind-merge` — `lib/cn.ts` says so and says why. So a base string
 * containing `border-line` and a caller adding `border-accent` puts BOTH in the class list, and which
 * one wins is decided by the order Tailwind happened to emit them in: a coin toss that changes
 * between builds. The same trap `Dialog` documents for its width.
 *
 * Structure, size and focus live here; every consumer states its own border, fill and text colour
 * exactly once. Nothing is ever overridden, so nothing can be ambiguous.
 *
 * ==========================================================================================
 * WHAT COUNTS AS ONE OF THESE
 * ==========================================================================================
 *
 * A control that says what you are LOOKING AT, rather than one that collects something: the whose
 * filter, the expenses table's search box, and its archived toggle. They are chrome, so the surface
 * behind them — a page's gradient, a panel — passes through, and a filled control in among them
 * reads as a field somebody forgot to complete. `TextField`'s filled `bg-input/30` is right for a
 * form and wrong here, which is why the search box is a bare `<input>` wearing this instead.
 *
 * `h-9` matches `Button`, so a filter beside a primary action in a title row sits on its line.
 */
export const filterControlClassName = [
  "h-9 rounded-control border px-3 text-xs transition-colors outline-none",
  "focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
].join(" ");

export interface FilterSelectProps {
  /** The accessible name, and the visible one when `showLabel`. Name the QUESTION: "Filter by whose". */
  label: string;
  /** Whether to render `label` beside the control as well as to assistive technology. */
  showLabel?: boolean;
  value: string;
  onChange: (next: string) => void;
  /** The `<option>` elements. */
  children: ReactNode;
  /** Required when `showLabel`, so the visible label points at this control and not another. */
  id?: string;
  testId?: string;
  className?: string;
}

export function FilterSelect({
  label,
  showLabel = false,
  value,
  onChange,
  children,
  id,
  testId,
  className,
}: FilterSelectProps): ReactNode {
  return (
    <span className={cn("inline-flex items-center gap-2", className)}>
      <label
        {...(id === undefined ? {} : { htmlFor: id })}
        className={showLabel ? "text-sm text-muted" : "sr-only"}
      >
        {label}
      </label>

      {/* The select and its drawn chevron, positioned together. */}
      <span className="relative inline-flex items-center">
        <select
          {...(id === undefined ? {} : { id })}
          aria-label={label}
          value={value}
          onChange={(event) => {
            onChange(event.target.value);
          }}
          {...(testId === undefined ? {} : { "data-testid": testId })}
          className={cn(
            filterControlClassName,
            SELECT_CHROME,
            // Its own two, and the only place this differs from `SelectField`: a filter is CHROME,
            // so the panel's own hairline and no fill at all.
            "border-line bg-transparent text-ink",
          )}
        >
          {children}
        </select>
        <SelectChevron />
      </span>
    </span>
  );
}
